import React, { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Activity, Users, Eye, Clock, BarChart3, RefreshCcw } from "lucide-react";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

interface TelemetryReport {
    loginStats: {
        totalLogins: number;
        uniqueUsers: number;
        loginsLast7Days: number;
        loginsLast30Days: number;
    };
    pageViewStats: {
        totalPageViews: number;
        avgDuration: number;
        viewsLast7Days: number;
    };
    topPages: { PageName: string; PagePath: string; views: number; avgDuration: number }[];
    recentSessions: { UserName: string; UserEmail: string; EventType: string; Timestamp: string; IpAddress: string }[];
    dailyLogins: { day: string; logins: number; uniqueUsers: number }[];
    pageViewsByUser: { UserEmail: string; pageViews: number; avgDuration: number }[];
    activeUsers: { UserName: string; UserEmail: string; lastSeen: string; sessionCount: number }[];
}

const SystemReport: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [report, setReport] = useState<TelemetryReport | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'pages' | 'users' | 'sessions'>('overview');

    const getToken = useCallback(async () => {
        return acquireTokenSafe(instance, accounts);
    }, [instance, accounts]);

    const fetchReport = useCallback(async () => {
        if (accounts.length === 0) return;
        setLoading(true);
        try {
            const token = await getToken();
            if (!token) { setLoading(false); return; }
            const res = await fetch(`${getApiBaseUrl()}/api/telemetry/report`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
            setReport(await res.json());
        } catch (err: any) {
            setError(err.message || 'Failed to load telemetry');
        } finally {
            setLoading(false);
        }
    }, [accounts, setLoading, getToken]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const tableStyles = {
        table: { style: { backgroundColor: 'var(--bg-secondary)' } },
        headRow: { style: { backgroundColor: 'var(--bg-tertiary)', borderBottomColor: 'var(--border-color)', fontWeight: 600, fontSize: '0.85rem' } },
        headCells: { style: { color: 'var(--text-secondary)' } },
        rows: { style: { backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderBottomColor: 'var(--border-color)' }, highlightOnHoverStyle: { backgroundColor: 'var(--bg-hover)', color: 'var(--text-primary)' } },
        cells: { style: { color: 'var(--text-primary)' } },
        pagination: { style: { backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)', borderTopColor: 'var(--border-color)' } },
    };

    const topPageColumns: TableColumn<TelemetryReport['topPages'][0]>[] = [
        { name: 'Page', selector: row => row.PageName, sortable: true, grow: 2 },
        { name: 'Path', selector: row => row.PagePath, width: '200px', cell: row => <span style={{ opacity: 0.6, fontSize: 12 }}>{row.PagePath}</span> },
        { name: 'Views', selector: row => row.views, sortable: true, width: '100px', right: true },
        { name: 'Avg Duration (s)', selector: row => row.avgDuration, sortable: true, width: '150px', right: true, cell: row => <span>{row.avgDuration != null ? Math.round(row.avgDuration) : '—'}</span> },
    ];

    const activeUserColumns: TableColumn<TelemetryReport['activeUsers'][0]>[] = [
        {
            name: 'User',
            cell: row => (
                <div>
                    <div style={{ fontWeight: 600 }}>{row.UserName}</div>
                    <div style={{ fontSize: 12, opacity: 0.6 }}>{row.UserEmail}</div>
                </div>
            ),
            sortable: true,
            grow: 2,
        },
        { name: 'Last Seen', selector: row => row.lastSeen, cell: row => new Date(row.lastSeen).toLocaleString(), sortable: true, width: '200px' },
        { name: 'Sessions', selector: row => row.sessionCount, sortable: true, width: '110px', right: true },
    ];

    const userViewColumns: TableColumn<TelemetryReport['pageViewsByUser'][0]>[] = [
        { name: 'User', selector: row => row.UserEmail, sortable: true, grow: 2 },
        { name: 'Page Views (30d)', selector: row => row.pageViews, sortable: true, width: '160px', right: true },
        { name: 'Avg Duration (s)', selector: row => row.avgDuration, sortable: true, width: '160px', right: true, cell: row => <span>{row.avgDuration != null ? Math.round(row.avgDuration) : '—'}</span> },
    ];

    const sessionColumns: TableColumn<TelemetryReport['recentSessions'][0]>[] = [
        { name: 'Time', selector: row => row.Timestamp, cell: row => new Date(row.Timestamp).toLocaleString(), sortable: true, width: '180px' },
        {
            name: 'User',
            cell: row => (
                <div>
                    <div style={{ fontWeight: 600 }}>{row.UserName}</div>
                    <div style={{ fontSize: 12, opacity: 0.6 }}>{row.UserEmail}</div>
                </div>
            ),
            grow: 2,
        },
        {
            name: 'Event',
            width: '120px',
            cell: row => <span className={`badge ${row.EventType === 'login' ? 'renewable' : 'one-off'}`}>{row.EventType}</span>,
        },
        { name: 'IP Address', selector: row => row.IpAddress || '—', width: '150px' },
    ];

    const dailyColumns: TableColumn<TelemetryReport['dailyLogins'][0]>[] = [
        { name: 'Date', selector: row => row.day, cell: row => new Date(row.day).toLocaleDateString(), sortable: true, width: '140px' },
        { name: 'Logins', selector: row => row.logins, sortable: true, width: '100px', right: true },
        { name: 'Unique Users', selector: row => row.uniqueUsers, sortable: true, width: '140px', right: true },
    ];

    if (error) {
        return (
            <div className="page-container">
                <div className="page-header">
                    <div className="header-title-section"><Activity size={28} /><h1>System Report</h1></div>
                </div>
                <div className="stat-card" style={{ background: 'var(--accent-red)', color: '#fff', padding: '16px 20px', marginTop: 20 }}>
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-title-section">
                    <Activity size={28} />
                    <div>
                        <h1>System Report</h1>
                        <p className="page-subtitle">Usage telemetry, page views, and session activity</p>
                    </div>
                </div>
                <button onClick={fetchReport} className="download-btn">
                    <RefreshCcw size={16} /> Refresh
                </button>
            </div>

            {report && (
                <>
                    {/* Stats cards */}
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-icon"><Users size={20} /></div>
                            <div className="stat-content">
                                <div className="stat-value">{report.loginStats.uniqueUsers}</div>
                                <div className="stat-label">Unique Users</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon"><Activity size={20} /></div>
                            <div className="stat-content">
                                <div className="stat-value">{report.loginStats.totalLogins}</div>
                                <div className="stat-label">Total Logins</div>
                            </div>
                        </div>
                        <div className="stat-card highlight">
                            <div className="stat-icon"><Activity size={20} /></div>
                            <div className="stat-content">
                                <div className="stat-value">{report.loginStats.loginsLast7Days}</div>
                                <div className="stat-label">Logins (7 days)</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon"><Eye size={20} /></div>
                            <div className="stat-content">
                                <div className="stat-value">{report.pageViewStats.totalPageViews}</div>
                                <div className="stat-label">Total Page Views</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon"><Eye size={20} /></div>
                            <div className="stat-content">
                                <div className="stat-value">{report.pageViewStats.viewsLast7Days}</div>
                                <div className="stat-label">Views (7 days)</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon"><Clock size={20} /></div>
                            <div className="stat-content">
                                <div className="stat-value">{report.pageViewStats.avgDuration != null ? Math.round(report.pageViewStats.avgDuration) : 0}s</div>
                                <div className="stat-label">Avg Page Duration</div>
                            </div>
                        </div>
                    </div>

                    {/* Tab navigation */}
                    <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid var(--border-color)' }}>
                        {[
                            { key: 'overview' as const, label: 'Overview', icon: <BarChart3 size={16} /> },
                            { key: 'pages' as const, label: 'Top Pages', icon: <Eye size={16} /> },
                            { key: 'users' as const, label: 'Users', icon: <Users size={16} /> },
                            { key: 'sessions' as const, label: 'Sessions', icon: <Activity size={16} /> },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                style={{
                                    padding: '10px 20px', background: 'none', border: 'none',
                                    borderBottom: activeTab === tab.key ? '2px solid var(--accent-blue)' : '2px solid transparent',
                                    color: activeTab === tab.key ? 'var(--accent-blue)' : 'var(--text-secondary)',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                                    fontWeight: activeTab === tab.key ? 600 : 400, fontSize: 14,
                                }}
                            >
                                {tab.icon} {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Overview: Daily logins */}
                    {activeTab === 'overview' && (
                        <div className="data-table-container">
                            <h3 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <BarChart3 size={18} /> Daily Login Activity (Last 30 Days)
                            </h3>
                            <DataTable columns={dailyColumns} data={report.dailyLogins} pagination paginationPerPage={15} highlightOnHover dense customStyles={tableStyles} noDataComponent={<div style={{ padding: 30, opacity: 0.5 }}>No login data</div>} />
                        </div>
                    )}

                    {/* Top Pages */}
                    {activeTab === 'pages' && (
                        <div className="data-table-container">
                            <h3 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Eye size={18} /> Most Viewed Pages
                            </h3>
                            <DataTable columns={topPageColumns} data={report.topPages} highlightOnHover dense customStyles={tableStyles} noDataComponent={<div style={{ padding: 30, opacity: 0.5 }}>No page view data</div>} />
                        </div>
                    )}

                    {/* Users tab */}
                    {activeTab === 'users' && (
                        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                            <div className="data-table-container" style={{ flex: 1, minWidth: 400 }}>
                                <h3 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Users size={18} /> Active Users
                                </h3>
                                <DataTable columns={activeUserColumns} data={report.activeUsers} highlightOnHover dense customStyles={tableStyles} noDataComponent={<div style={{ padding: 30, opacity: 0.5 }}>No user data</div>} />
                            </div>
                            <div className="data-table-container" style={{ flex: 1, minWidth: 400 }}>
                                <h3 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Eye size={18} /> Page Views by User (30 days)
                                </h3>
                                <DataTable columns={userViewColumns} data={report.pageViewsByUser} highlightOnHover dense customStyles={tableStyles} noDataComponent={<div style={{ padding: 30, opacity: 0.5 }}>No data</div>} />
                            </div>
                        </div>
                    )}

                    {/* Sessions tab */}
                    {activeTab === 'sessions' && (
                        <div className="data-table-container">
                            <h3 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Activity size={18} /> Recent Sessions
                            </h3>
                            <DataTable columns={sessionColumns} data={report.recentSessions} pagination paginationPerPage={20} highlightOnHover dense customStyles={tableStyles} noDataComponent={<div style={{ padding: 30, opacity: 0.5 }}>No session data</div>} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default SystemReport;
